import React, {useContext, useEffect, useState} from "react";
import {ArdaiAPIContext} from "../webapi/WebAPI";
import {ArdaiAPIStateContext} from "../ArdaiMain";
import {Project} from "../data/Project";
import {DescriptiveTypography} from "../../triton/components/typography/DescriptiveTypography";
import {MainTypography} from "../../triton/components/typography/MainTypography";
import {
    AddRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    CloseRounded,
    CopyAllRounded,
    DataObjectRounded,
    DataObjectSharp,
    DeleteRounded,
    DownloadRounded,
    EditRounded,
    EmojiObjectsRounded,
    FormatShapesRounded,
    FullscreenExitRounded, FullscreenRounded,
    MoreVertRounded,
    RefreshRounded,
    SecurityRounded,
    SelectAllRounded,
    TextFieldsRounded
} from "@mui/icons-material";
import {IconButton} from "./IconButton";
import {Tag} from "./Tag";
import {DuplexEventRelay} from "../utils/DuplexEventRelay";
import {Menu} from "./Menu";
import {MenuButton} from "./MenuButton";
import {MenuDivider} from "@szhsin/react-menu";
import {BooleanContext} from "../../triton/components/BooleanContext";
import {ProjectEditDialog, ProjectEditDialogProps} from "./ProjectEditDialog";
import {webDB} from "../webapi/WebAPIDB";
import {ButtonModalCompound} from "./ButtonModalCompound";
import {DangerConfirmationDialog} from "./dialogs/DangerConfirmationDialog";
import {StyledModal} from "./StyledModal";
import {ButtonBase, ButtonVariant} from "../../triton/components/buttons/ButtonBase";
import {Grid} from "../../triton/components/layouts/Grid";
import {Formik} from "formik";
import {FormElement} from "../../triton/components/forms/FormElement";
import {FormikSingleLineInput} from "../../triton/components/forms/FormikSingleLineInput";
import {FormikFormCheckbox} from "./FormikFormCheckbox";
import {FormikAdvancedFormCheckbox} from "./FormikAdvancedFormCheckbox";
import {FormDivider} from "../../triton/components/forms/FormDivider";
import {InlineEditable} from "../../triton/components/typography/InlineEditable";
import {CollapseIconButton} from "./CollapseIconButton";
import {TransitionGroup} from "react-transition-group";
import Collapse from "@mui/material/Collapse";
import {MetaBasicEntry} from "./MetaBasicEntry";
import {ErrorMessage} from "../../triton/components/forms/ErrorMessage";
import {useLiveQuery} from "dexie-react-hooks";
import {Property} from "../data/Property";
import {as} from "../../atlas/Lang";
import {TabBar} from "./TabBar";
import {StringField} from "../testing/StringField";
import {PropertyDisplay} from "./PropertyDisplay";
import {FlatIconButton} from "./FlatIconButton";
import {KVEntryDisplay} from "./KVEntryDisplay";
import {TabBodyRenderer} from "./TabBodyRenderer";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/scss/image-gallery.scss";

export type ProjectHeaderViewState = {
    editDialogOpened: boolean,
    detailsTrayExpanded: boolean,
    activeTab: string
}

export const ProjectHeaderView: React.FC = props =>  {
    const api = useContext(ArdaiAPIContext);
    const state = useContext(ArdaiAPIStateContext);
    const defaultTab = "details";
    const [localState, setLocalState] = useState<ProjectHeaderViewState>({
        editDialogOpened: false,
        detailsTrayExpanded: false,
        activeTab: defaultTab
    });
    const [currentProject, setCurrentProject] = useState<Project | undefined>(undefined);
    useEffect(() => {
        api.getCurrentProject().then(cp => setCurrentProject(cp));
    }, [api, state]);

    const liveProjectData = useLiveQuery(() => {
        // currentProject?.id!
        if (!state.selectedProject) return undefined;
        return webDB.projects.get(state.selectedProject!);
    }, [state.selectedProject]);

    const resourceCount = liveProjectData?.resources.length ?? 0;

    const [projectImages, setProjectImages] = useState<any[]>([]);

    useEffect(() => {
        // TODO: Close
        if (state.selectedProject === undefined) return;

        api.getImageSelectionFromProjectID(state.selectedProject).then(async selection => {
            setProjectImages((await Promise.all(selection.imagesIDs.map(async id => {
                const img = await webDB.images.get(id);
                if (img === undefined) return undefined;
                return URL.createObjectURL(img.data)
            }))).filter(url => url !== undefined))
        })
    }, [state.selectedProject]);

    return (
        state.selectedProject === undefined ? (
            <div style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
            }}>
                <DescriptiveTypography text={"Select a project"}/>
            </div>
        ) : (
            <>
                { localState.editDialogOpened && (
                    <ProjectEditDialog
                        open={localState.editDialogOpened}
                        for={currentProject?.id!}
                        onSave={project => {}}
                        onCancel={() => {
                            setLocalState(prevState => ({ ...prevState, editDialogOpened: false }))
                        }}
                    />
                ) }

                <div style={{
                    display: "grid",
                    // gap: "8px"
                }}>
                    <div style={{
                        display: "grid",
                        alignItems: "center",
                        gridTemplateColumns: "auto min-content"
                    }}>
                        <InlineEditable
                            placeholder={"Title"}
                            useTextStylePreset={"main"}
                            initialValue={currentProject?.title!}
                            onChange={async value => {
                                await webDB.projects.update(currentProject?.id!, {
                                    title: value
                                })
                            }}
                        />

                        <div style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            height: "100%",
                            gap: "4px"
                        }}>
                            <Menu opener={
                                <FlatIconButton children={<MoreVertRounded fontSize={"small"}/>}/>
                            }>
                                <MenuButton text={"Edit"} icon={<EditRounded/>} appendix={"Ctrl+E"} onSelect={() => {
                                    setLocalState(prevState => ({ ...prevState, editDialogOpened: true }))
                                }}/>

                                <ButtonModalCompound button={<MenuButton text={"Clone project"} icon={<CopyAllRounded/>}/>} modalContent={ctx => (
                                    <Formik initialValues={{ title: "", copyImages: true }} onSubmit={values => {

                                        // TODO: Implement


                                        ctx.close();
                                    }} children={fp => (
                                        <StyledModal title={"Clone project"} icon={<CopyAllRounded/>} children={
                                            <>
                                                <FormElement title={"New project title"} caption={"Leave empty to use auto-generated project name."} children={
                                                    <FormikSingleLineInput
                                                        name={"title"}
                                                        formikProps={fp}
                                                        placeholder={`${currentProject?.title}-copy`}
                                                        autoFocus
                                                    />
                                                }/>

                                                <FormDivider title={"Clone settings"}/>

                                                <FormikAdvancedFormCheckbox
                                                    formik={fp}
                                                    defaultValue={true}
                                                    title={"Deep-copy images"}
                                                    name={"copyImages"}
                                                    caption={"It's highly recommended to deep-copy images when cloning a project. More details in checkbox documentation"}
                                                    documentation={
                                                        `Not deep-copying images means changes to images itself will afflict changes 
                                                        to all projects containing the image. For example: Deleting an image in a cloned project, 
                                                        which wasn't deep-copied, results in the image being deleted from all 
                                                        other projects containing this specific image.`
                                                    }
                                                />
                                            </>
                                        } footer={
                                            <Grid style={{ width: "100%" }} gTC={"repeat(2, 1fr)"}>
                                                <ButtonBase text={"Duplicate [TODO: Implement]"} baseProps={{
                                                    onClick: (e) => fp.handleSubmit(e)
                                                }}/>
                                                <ButtonBase text={"Cancel"} baseProps={{
                                                    onClick: () => ctx.close()
                                                }}/>
                                            </Grid>
                                        }/>
                                    )}/>
                                )}/>

                                <ButtonModalCompound button={<MenuButton text={"Delete all images"} icon={<DeleteRounded/>}/>} modalContent={ctx => (
                                    <DangerConfirmationDialog
                                        title={"Delete all images"}
                                        description={"Delete all images in this project. Images deleted this way can't be retrieved again!"}
                                        onCancel={() => ctx.close()}
                                        onConfirmation={() => {
                                            if (currentProject === undefined) {
                                                window.alert("Cannot bulk delete images, if the current project is undefined");
                                                return;
                                            }

                                            webDB.projects.update(currentProject?.id, {
                                                "resources": []
                                            })

                                            webDB.images.bulkDelete(currentProject?.resources ?? []).then(() => {
                                                ctx.close();
                                            });
                                        }}
                                    />
                                )}/>

                                <MenuButton disabled text={"Delete project"} icon={<DeleteRounded/>}/>

                                <MenuButton text={"Select all"} icon={<SelectAllRounded/>} onSelect={() => {
                                    api.selectionManager.select(currentProject?.resources ?? []);
                                }}/>

                                <MenuButton text={"Select duplicates"} icon={<SelectAllRounded/>} onSelect={() => {
                                    api.selectionManager.selectDuplicates(currentProject?.resources ?? []);
                                }}/>

                                <MenuButton text={"Download images"} icon={<DownloadRounded/>} onSelect={() => {
                                    if (currentProject?.id === undefined) return;
                                    api.downloadManager.downloadProject(currentProject.id, new DuplexEventRelay()).then(() => {});
                                }}/>

                                <MenuDivider/>

                                <MenuButton text={"Remove cover image"} onSelect={() => {
                                    api.getProjectContext().setPreviewImage(undefined);
                                }}/>

                                {
                                    currentProject?.previewImageID === undefined ? undefined : (
                                        <MenuButton text={"Open cover image"} onSelect={() => {
                                            api.selectImageByID(currentProject?.previewImageID!, false)
                                        }}/>
                                    )
                                }

                                <MenuDivider/>

                                <MenuButton text={"Clear all properties"} disabled={(liveProjectData?.metaProperties ?? []).length === 0} onSelect={async () => {
                                    await api.getProjectContext().clearProperties();
                                }}/>
                            </Menu>

                            <CollapseIconButton
                                open={localState.detailsTrayExpanded}
                                onToggle={open => setLocalState(prevState => ({
                                    ...prevState,
                                    detailsTrayExpanded: open
                                }))}
                            />

                            <IconButton size={"small"} tooltip={"Close"} children={<CloseRounded/>} onClick={() => api.closeProject()}/>
                        </div>
                    </div>

                    { currentProject?.description && (
                        <DescriptiveTypography text={currentProject?.description} style={{
                            marginTop: 8
                        }}/>
                    ) }

                    { currentProject?.tags && currentProject.tags.length > 0 && (
                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            flexDirection: "row",
                            gap: "4px",
                            width: "100%",
                            marginTop: 8
                        }}>
                            { currentProject?.tags.map(tag => (
                                <Tag key={tag} tag={tag} onClick={() => {
                                    // TODO: Search for all images with this tag
                                }}/>
                            ))}
                        </div>
                    ) }




                    <TransitionGroup children={localState.detailsTrayExpanded && (
                        <Collapse children={
                            <div style={{
                                display: "grid",
                                gap: 8,
                                marginTop: 16,
                                gridTemplateRows: "min-content auto"
                            }}>

                                <TabBar activeBar={localState.activeTab} equalSizeMode onTabChange={tab => {
                                    setLocalState(prevState => ({
                                        ...prevState,
                                        activeTab: tab
                                    }));
                                }} tabs={[
                                    { id: "details", title: "Details" },
                                    { id: "properties", title: "Properties" },
                                    { id: "resources", title: "", icon: <DataObjectRounded/> }
                                ]}/>

                                <TabBodyRenderer active={localState.activeTab} tabs={new Map<string, () => React.ReactElement>([
                                    // Details
                                    ["details", () => (
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 4
                                        }}>
                                            <MainTypography text={"Details"}/>

                                            <KVEntryDisplay
                                                title={"Resource count"}
                                                value={resourceCount === 0 ? "Empty" : String(resourceCount)}
                                            />
                                        </div>
                                    )],

                                    // Properties
                                    ["properties", () => (
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 8
                                        }}>
                                            <div style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: 4
                                            }}>

                                                <MainTypography text={"Properties"}/>

                                                {/* Map all properties */}
                                                <TransitionGroup children={ liveProjectData?.metaProperties?.sort((a, b) => a.key < b.key ? -1 : 1).map(property => (
                                                    <Collapse key={property.key} children={
                                                        <div style={{
                                                            marginTop: 4
                                                        }}>
                                                            <PropertyDisplay property={property}/>
                                                        </div>
                                                    }/>
                                                )) }/>

                                                {/* No-properties notice */}
                                                <TransitionGroup children={ (liveProjectData?.metaProperties?.length ?? 0) === 0 && (
                                                    <Collapse children={
                                                        <div style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            width: "100%",
                                                            padding: 8
                                                        }} children={
                                                            <DescriptiveTypography noSelect text={"No properties"}/>
                                                        }/>
                                                    }/>
                                                ) }/>
                                            </div>

                                            <ButtonModalCompound button={
                                                <ButtonBase
                                                    baseProps={{ type: "button", style: { width: "100%" } }}
                                                    text={"Add property"}
                                                />
                                            } modalContent={ctx => (
                                                <Formik initialValues={{ key: "", value: "" }} onSubmit={(v, h) => {
                                                    webDB.projects.update(currentProject?.id!, {
                                                        metaProperties: as<Array<Property>>([
                                                            ...liveProjectData?.metaProperties ?? [], {
                                                                key: v.key,
                                                                value: v.value,
                                                            }
                                                        ])
                                                    });
                                                }} validate={v => {
                                                    const e: Partial<typeof v> = {};
                                                    if (v.key.trim().length === 0) e.key = "Properties cannot have a blank keys";
                                                    return e;
                                                }} children={fp => (
                                                    <StyledModal title={"Add property"} icon={<TextFieldsRounded/>} onClose={ctx.close} children={
                                                        <Grid>
                                                            <FormElement>
                                                                <FormikSingleLineInput
                                                                    name={"key"}
                                                                    placeholder={"Key"}
                                                                    autoFocus
                                                                    formikProps={fp}
                                                                />
                                                                <ErrorMessage formikProps={fp} name={"key"}/>
                                                            </FormElement>

                                                            <FormikSingleLineInput
                                                                name={"value"}
                                                                placeholder={"Value"}
                                                                formikProps={fp}
                                                            />
                                                        </Grid>
                                                    } footer={
                                                        <Grid style={{ width: "100%" }} gTC={"min-content auto min-content"}>
                                                            <IconButton children={<AddRounded/>} size={"small"} tooltip={"Add and keep open"} onClick={() => {
                                                                fp.handleSubmit();
                                                            }}/>
                                                            <ButtonBase text={"Add"} baseProps={{
                                                                type: "button",
                                                                onClick: (e) => {
                                                                    fp.handleSubmit(e);
                                                                    ctx.close();
                                                                }
                                                            }}/>
                                                            <Menu children={
                                                                <MenuButton text={"Reset form"} icon={<RefreshRounded/>} onSelect={() => {
                                                                    fp.resetForm({
                                                                        values: {
                                                                            key: "",
                                                                            value: ""
                                                                        }
                                                                    });
                                                                }}/>
                                                            }/>
                                                        </Grid>
                                                    }/>
                                                )}/>
                                            )}/>
                                        </div>
                                    )],


                                    ["resources", () => (
                                        <div style={{
                                            overflow: "hidden",
                                            overscrollBehavior: "none",
                                            touchAction: "none",
                                            position: "relative",
                                        }}>
                                            <ImageGallery
                                                showThumbnails={false}
                                                renderLeftNav={(onClick, disabled) => (
                                                    <div style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        left: 0,
                                                        zIndex: 2
                                                    }} children={
                                                        <FlatIconButton
                                                            onClick={onClick}
                                                            deactivated={disabled}
                                                            children={
                                                                <ChevronLeftRounded fontSize={"small"}/>
                                                            }
                                                        />
                                                    }/>
                                                )}
                                                renderRightNav={(onClick, disabled) => (
                                                    <div style={{
                                                        position: "absolute",
                                                        top: 0,
                                                        right: 0,
                                                        zIndex: 2
                                                    }} children={
                                                        <FlatIconButton
                                                            onClick={onClick}
                                                            deactivated={disabled}
                                                            children={
                                                                <ChevronRightRounded fontSize={"small"}/>
                                                            }
                                                        />
                                                    }/>
                                                )}
                                                renderFullscreenButton={(onClick, isFullscreen) => (
                                                    <div style={{
                                                        position: "absolute",
                                                        bottom: 0,
                                                        right: 0,
                                                        zIndex: 2
                                                    }} children={
                                                        <FlatIconButton
                                                            onClick={onClick}
                                                            children={
                                                                isFullscreen ? (
                                                                    <FullscreenExitRounded fontSize={"small"}/>
                                                                ) : (
                                                                    <FullscreenRounded fontSize={"small"}/>
                                                                )
                                                            }
                                                        />
                                                    }/>
                                                )}
                                                showPlayButton={false}
                                                items={projectImages.map(img => ({
                                                    original: img,
                                                    thumbnail: img,
                                                    fullscreen: img,
                                                    originalHeight: 250,
                                                    thumbnailHeight: 50,
                                                }))}
                                            />
                                        </div>
                                    )]
                                ])}/>
                            </div>
                        }/>
                    )}/>




                </div>
            </>
        )
    );
}
